import { LoggerService } from '@/utils/LoggerService';
import { useTokenStore } from '@authvia-v3/pinia-stores-root';
import type { RouteParams } from 'vue-router';
import {
  getAuthenticatedUserId,
  getAuthenticatedUserRole,
  LocalAuthHandler,
} from '@/utils';

async function getSentry(): Promise<typeof import('@sentry/vue')> {
  return import('@sentry/vue');
}

export class SentryService {
  private static logger = new LoggerService('[SentryService]');
  /**
   * Sets the user information in Sentry based on the current token and route params
   */
  public static async setUserFromToken(routeParams?: RouteParams): Promise<void> {
    try {
      const Sentry = await getSentry();
      const tokenStore = useTokenStore();
      const token = tokenStore?.token || LocalAuthHandler.getToken();
      if (!token) {
        Sentry.setUser(null);
        return;
      }
      const payload = tokenStore.parse(token);
      const userId = getAuthenticatedUserId(payload);
      const userRole = getAuthenticatedUserRole(payload);
      const merchantId = routeParams?.avid as string | undefined;
      const customerRef = routeParams?.ref as string | undefined;
      const businessProcessId = routeParams?.id as string | undefined;
      if (userId) {
        const userData: Record<string, string> = {
          id: userId,
          role: userRole,
        };
        if (merchantId) {
          userData.avid = merchantId;
        }
        if (customerRef) {
          userData.customer_ref = customerRef;
        }
        if (businessProcessId) {
          userData.business_process_id = businessProcessId;
        }
        Sentry.setUser(userData);
        const tags: Record<string, string> = {};
        if (businessProcessId) {
          tags.business_process_id = businessProcessId;
        }
        if (merchantId) {
          tags.avid = merchantId;
        }
        if (customerRef) {
          tags.customer_ref = customerRef;
        }
        if (Object.keys(tags).length > 0) {
          Sentry.setTags(tags);
        }
      } else {
        Sentry.setUser(null);
      }
    } catch (error: unknown) {
      this.logger.error(
        'setUserFromToken() - Error setting user in Sentry',
        error,
      );
    }
  }
  /**
   * Clears the user information from Sentry (used on logout)
   */
  public static async clearUser(): Promise<void> {
    try {
      const Sentry = await getSentry();
      this.logger.debug('clearUser() - Clearing user from Sentry');
      Sentry.setUser(null);
    } catch (error: unknown) {
      this.logger.error('clearUser() - Error clearing user from Sentry', error);
    }
  }
}
